// ============================================================================
// Stop Service — the ONE screen that stops service at a facility.
//
// WHY THIS EXISTS
// ---------------
// Until now the only lifecycle action in the app was "Terminate account",
// buried in the facility card's Actions tab. Two things were wrong with that:
//
//   1. It is an ACCOUNT action reached from a FACILITY. Ben, 2026-08-20: "this
//      has already hurt us several times because we were conflating account
//      and facility. We almost never want to cancel an account and almost
//      always want to cancel a facility."
//   2. There was no way to pause anything. An AM whose client is shut for a
//      three-month renovation had two options: leave the facility looking
//      fully serviced — so the sub keeps being paid and the client keeps being
//      billed — or terminate it, which ends the relationship record and hard-
//      Ends the sub's assignment. Ben was doing these by hand.
//
// So the first question here is not "which account?" but "is service stopping
// for now, or for good?" — a two-way split over one facility. Both branches
// stop service; only one of them is a decision about the relationship.
//
// WHAT IT IS NOT
// --------------
// Not a second copy of the termination workflow. Terminate calls
// rpc_end_facility, which delegates to rpc_create_account_termination — the
// same Finance/HR/executive ack chain, the same one-open-termination rule, the
// same scheduled close on the final service date. NOTHING CLOSES TODAY. The
// ack panel stays in the Actions tab, which is where this screen sends you.
//
// Two entry points, one page: the Actions tab and the Info tab both LINK here
// (Ben: "not two identical pages. Just one page that gets linked to it from
// another location").
//
// WHAT IT DOES NOT WRITE
// ----------------------
// Nothing, directly. Every mutation goes through an RPC, because pausing is
// not one UPDATE: it snapshots the facility, suspends exactly the sub
// assignments it snapshotted, raises a stop-billing notice for Finance and
// emits one routed notification. An inline edit of service_state would set a
// badge and leave the crew rostered.
//
// The presentational pieces and the hint→field map live in
// `stop-service-parts.tsx` so they can be server-rendered in a test without
// stubbing the Supabase client — TCSD-0049, "a fix nobody can look at is not
// verified".
// ============================================================================
import { canOpenTermination, canPauseService } from '../../shared/role-gates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CircleSlash, Info, PauseCircle, PlayCircle } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import { Button, ErrorState, PageHeader, Skeleton, useToast } from '../../shared/components';
import { describeError } from '../../shared/errors';
import {
  clientRateDisplay,
  deriveRateBasisFallback,
  normalizeRateBasis,
  type RateBasis,
} from '../../shared/rate-basis';
import type { Facility } from '../../shared/types';
import { TerminationRecord } from './termination-record';
// The three Stop Service saves, shared with Ask AI's building card (item 944).
import { endFacility, pauseFacility, resumeFacility } from './lib/building-saves';
import { readStopServicePrefill } from './lib/stop-service-link';
import {
  BranchOption,
  FieldError,
  INPUT_CLASS,
  LifecycleDates,
  OtherClosures,
  OverridesDisclosure,
  PAUSE_BRANCH_BODY,
  ReasonDefaults,
  ServiceStateChip,
  StateNotice,
  TERMINATE_BRANCH_BODY,
  coversFacilityFilter,
  deriveStopServiceCapabilities,
  fieldErrorFrom,
  fmtDate,
  lifecycleDateRows,
  pausedRateSentence,
  terminationCoversFacility,
  type Branch,
  type FormField,
  type LifecycleReason,
  type OpenTermination,
  type OtherClosureRow,
} from './stop-service-parts';

type OpenTerminationWithAcks = OpenTermination & {
  approved: boolean | null;
  finance_ack: boolean | null;
  hr_ack: boolean | null;
};

// Who may open a termination. Mirrors rpc_create_account_termination's own
// check (TCS_TERM_FORBIDDEN_ROLE) so the button is absent rather than the
// error being a surprise. The RPC remains the authority — this only decides
// what to render.

// Pause is open to any active TCS employee: rpc_pause_facility gates on
// is_tcs_employee(), not on a named role. A pause is reversible, snapshotted
// and notified — the cost of a wrong one is low, and the cost of an AM not
// being able to record one is what put us here.

// ============================================================================
// The page
// ============================================================================
export default function StopService() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { profile } = useAuth();
  const role = profile?.role;

  const [facility, setFacility] = useState<Facility | null>(null);
  const [accountName, setAccountName] = useState<string | null>(null);
  const [pauseReasons, setPauseReasons] = useState<LifecycleReason[]>([]);
  const [endReasons, setEndReasons] = useState<LifecycleReason[]>([]);
  const [openTermination, setOpenTermination] = useState<OpenTermination | null>(null);
  // Open terminations on OTHER facilities of this account — shown as rows, not
  // as blockers (one open termination per facility, 2026-10-13).
  const [otherClosures, setOtherClosures] = useState<OtherClosureRow[]>([]);
  // Bumped after a termination is opened so the sign-off panel re-reads.
  const [recordKey, setRecordKey] = useState(0);
  // A SECOND termination read, deliberately. `openTermination` answers "is one
  // still open?" and drives the capability gate, so it excludes Completed.
  // But the planned-vs-actual pair Finance needs only becomes interesting ONCE
  // the close has run — at which point the open query has already dropped the
  // record carrying the planned date. Two questions, two filters.
  const [datedTermination, setDatedTermination] = useState<OpenTermination | null>(null);
  // Why this building has (or has not) a monthly rate — TCSD-0049. Read from
  // v_facility_margins like the facility card does, because facilities.monthly_rate
  // alone cannot tell an hourly hospital from a genuine data gap.
  const [rateBasis, setRateBasis] = useState<RateBasis | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [branch, setBranch] = useState<Branch | null>(null);
  const [pauseReasonCode, setPauseReasonCode] = useState('');
  const [resumeDate, setResumeDate] = useState('');
  const [note, setNote] = useState('');
  const [endReasonCode, setEndReasonCode] = useState('');
  const [finalDate, setFinalDate] = useState('');
  const [showOverrides, setShowOverrides] = useState(false);
  const [intentOverride, setIntentOverride] = useState('');
  const [attributionOverride, setAttributionOverride] = useState('');
  const [resumeNote, setResumeNote] = useState('');
  const [confirmTerminate, setConfirmTerminate] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<FormField, string>>>({});

  // Ask AI opens this page with what was said filled in (Ben 2026-10-27, option
  // 1): applied once, after the lists load, and only where the page would offer
  // it. Nothing is saved until this page's own button is pressed.
  const [params] = useSearchParams();
  const prefill = useMemo(() => readStopServicePrefill(params), [params]);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [filledFromAskAi, setFilledFromAskAi] = useState(false);

  const facilityHref = `/account-management/facilities/${facilityId ?? ''}`;

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setLoadError(null);
    try {
      // Flat fetch, then look up — the house data pattern. No PostgREST embeds.
      // `select('*')` deliberately: this screen needs eight lifecycle columns
      // and the facility card already pays for the same row shape.
      const fac = await supabase
        .from('facilities')
        .select('*')
        .eq('facility_id', facilityId)
        .maybeSingle<Facility>();
      if (fac.error) throw fac.error;
      const f = fac.data ?? null;
      setFacility(f);

      const [reasons, acct, term, margin, datedTerm] = await Promise.all([
        supabase
          .from('facility_lifecycle_reasons')
          .select('reason_code, kind, label, default_intent, default_attribution')
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        f?.account_id
          ? supabase
              .from('accounts')
              .select('account_name')
              .eq('account_id', f.account_id)
              .maybeSingle<{ account_name: string | null }>()
          : Promise.resolve({ data: null, error: null }),
        // Every OPEN termination on the account — open meaning the nightly close
        // has not applied it and it is not cancelled, the RPC's own definition.
        // Since 2026-10-13 the rule is one per FACILITY (TCS_TERM_ALREADY_OPEN
        // fires only when this facility is already covered), so the page splits
        // these into "covers this facility" and "other closures on this account".
        f?.account_id
          ? supabase
              .from('account_terminations')
              .select(
                'termination_id, status, scope, facility_ids, final_service_date, approved, finance_ack, hr_ack',
              )
              .eq('account_id', f.account_id)
              .is('applied_at', null)
              .neq('status', 'Cancelled')
              .order('created_at', { ascending: false })
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from('v_facility_margins')
          .select('rate_basis')
          .eq('facility_id', facilityId)
          .maybeSingle<{ rate_basis: string | null }>(),
        // Latest NON-CANCELLED termination COVERING THIS FACILITY, Completed
        // included, for the planned-vs-actual dates. Reading the account's
        // latest instead put Denver's Oct 31 on Mile High's page (2026-10-13).
        f?.account_id
          ? supabase
              .from('account_terminations')
              .select('termination_id, status, scope, facility_ids, final_service_date')
              .eq('account_id', f.account_id)
              .or(coversFacilityFilter(facilityId))
              .not('status', 'in', '("Cancelled")')
              .order('created_at', { ascending: false })
              .limit(1)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (reasons.error) throw reasons.error;
      const rows = (reasons.data ?? []) as Array<LifecycleReason & { kind: string }>;
      setPauseReasons(rows.filter((r) => r.kind === 'pause'));
      setEndReasons(rows.filter((r) => r.kind === 'end'));
      setAccountName(acct.data?.account_name ?? null);

      const open = (term.data ?? []) as OpenTerminationWithAcks[];
      setOpenTermination(open.find((t) => terminationCoversFacility(t, facilityId)) ?? null);
      const others = open.filter((t) => !terminationCoversFacility(t, facilityId));
      const otherIds = Array.from(new Set(others.flatMap((t) => t.facility_ids ?? [])));
      const names = new Map<string, string>();
      if (otherIds.length > 0) {
        const facs = await supabase
          .from('facilities')
          .select('facility_id, facility_name')
          .in('facility_id', otherIds);
        for (const x of (facs.data ?? []) as Array<{ facility_id: string; facility_name: string | null }>) {
          names.set(x.facility_id, x.facility_name ?? x.facility_id);
        }
      }
      setOtherClosures(
        others.map((t) => {
          const ids = t.facility_ids ?? [];
          return {
            termination_id: t.termination_id,
            facility_id: ids[0] ?? null,
            facility_names: ids.map((id) => names.get(id) ?? id).join(', ') || t.termination_id,
            final_service_date: t.final_service_date,
            status: t.status,
            approved: !!t.approved,
            finance_ack: !!t.finance_ack,
            hr_ack: !!t.hr_ack,
          };
        }),
      );
      setDatedTermination(((datedTerm.data ?? []) as OpenTermination[])[0] ?? null);
      // The view excludes Closed and Merged rows, so a miss is expected rather
      // than an error. deriveRateBasisFallback refuses to guess 'hourly' from a
      // NULL rate and says 'unrated' instead, which is the honest answer.
      setRateBasis(
        margin.data?.rate_basis != null
          ? normalizeRateBasis(margin.data.rate_basis)
          : deriveRateBasisFallback(f?.monthly_rate),
      );
    } catch (err) {
      const d = describeError(err, 'Could not load this facility.');
      console.error('[stop-service] load', d.message, err);
      setLoadError(d.message);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    void load();
  }, [load]);

  // One derivation, shared with the entry card on the facility detail. NULL
  // service_state means "never touched by the pause RPCs", which is Active —
  // resolved inside deriveStopServiceCapabilities so no caller has to remember.
  const caps = deriveStopServiceCapabilities({
    status: facility?.status,
    serviceState: facility?.service_state,
    rolePause: canPauseService(role),
    roleTerminate: canOpenTermination(role),
    openTerminationId: openTermination?.termination_id ?? null,
  });
  const { isPaused, isEnded, isRecordClosed } = caps;

  useEffect(() => {
    if (!prefill || prefillApplied || loading || !facility) return;
    setPrefillApplied(true);
    let used = false;
    if (prefill.branch === 'pause' && caps.canOfferPause) {
      setBranch('pause');
      if (pauseReasons.some((r) => r.reason_code === prefill.reasonCode)) setPauseReasonCode(prefill.reasonCode);
      if (prefill.date) setResumeDate(prefill.date);
      if (prefill.note) setNote(prefill.note);
      used = true;
    } else if (prefill.branch === 'terminate' && caps.canOfferTerminate) {
      setBranch('terminate');
      if (endReasons.some((r) => r.reason_code === prefill.reasonCode)) setEndReasonCode(prefill.reasonCode);
      if (prefill.date) setFinalDate(prefill.date);
      if (prefill.note) setNote(prefill.note);
      used = true;
    } else if (!prefill.branch && caps.canOfferResume && prefill.note) {
      setResumeNote(prefill.note);
      used = true;
    }
    setFilledFromAskAi(used);
  }, [prefill, prefillApplied, loading, facility, caps.canOfferPause, caps.canOfferTerminate, caps.canOfferResume, pauseReasons, endReasons]);

  const pauseReasonRow = useMemo(
    () => pauseReasons.find((r) => r.reason_code === pauseReasonCode) ?? null,
    [pauseReasons, pauseReasonCode],
  );
  const endReasonRow = useMemo(
    () => endReasons.find((r) => r.reason_code === endReasonCode) ?? null,
    [endReasons, endReasonCode],
  );
  const activePauseReasonLabel = useMemo(
    () =>
      pauseReasons.find((r) => r.reason_code === facility?.pause_reason)?.label ??
      facility?.pause_reason ??
      null,
    [pauseReasons, facility?.pause_reason],
  );

  // `openTermination` is now, by construction, the open termination covering
  // THIS facility (named in it, or account-wide). Sibling closures are in
  // `otherClosures` and never block this page.
  const openTerminationCoversThis = !!openTermination;

  function clearError(field: FormField) {
    setErrors((x) => ({ ...x, [field]: undefined, form: undefined }));
  }

  // ----- Actions -----------------------------------------------------------
  // The client-side checks below duplicate the RPC's own guards ON PURPOSE, and
  // only where the RPC's message would arrive after a round trip for something
  // the form already knows. The RPC stays the authority: anything it rejects is
  // rendered from its hint, never pre-empted by guesswork.

  async function submitPause() {
    if (!facilityId) return;
    if (!pauseReasonCode) {
      setErrors({ pauseReason: 'Pick a pause reason.' });
      return;
    }
    if (!resumeDate && !note.trim()) {
      setErrors({
        resumeDate:
          'Give an expected resume date, or write a note. An open-ended pause with no explanation is how a paused client becomes an unrecorded loss.',
      });
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      // Optional args are typed `?: string`, so they are OMITTED rather than
      // passed as null — sending null would override the reason's default
      // attribution/intent with nothing and trip TCS_PAUSE_BAD_INTENT.
      const args: {
        p_facility_id: string;
        p_pause_reason_code: string;
        p_expected_resume_date?: string;
        p_note?: string;
        p_attribution?: string;
        p_relationship_intent?: string;
      } = { p_facility_id: facilityId, p_pause_reason_code: pauseReasonCode };
      if (resumeDate) args.p_expected_resume_date = resumeDate;
      if (note.trim()) args.p_note = note.trim();
      if (attributionOverride) args.p_attribution = attributionOverride;
      if (intentOverride) args.p_relationship_intent = intentOverride;

      const data = await pauseFacility(args);
      // duration 0 = stays until dismissed. The RPC's return string names the
      // snapshot id and the assignment count, which is the receipt.
      toast.success('Service paused', {
        description: typeof data === 'string' ? data : undefined,
        duration: 0,
      });
      navigate(facilityHref);
    } catch (err) {
      const { field, message } = fieldErrorFrom(err, 'Could not pause this facility.');
      setErrors({ [field]: message });
    } finally {
      setBusy(false);
    }
  }

  async function submitResume() {
    if (!facilityId) return;
    setBusy(true);
    setErrors({});
    try {
      const args: { p_facility_id: string; p_note?: string } = { p_facility_id: facilityId };
      if (resumeNote.trim()) args.p_note = resumeNote.trim();
      const data = await resumeFacility(args);
      toast.success('Service resumed', {
        description: typeof data === 'string' ? data : undefined,
        duration: 0,
      });
      navigate(facilityHref);
    } catch (err) {
      const { field, message } = fieldErrorFrom(err, 'Could not resume this facility.');
      setErrors({ [field]: message });
    } finally {
      setBusy(false);
    }
  }

  async function submitTerminate() {
    if (!facilityId) return;
    if (!endReasonCode) {
      setErrors({ endReason: 'Pick an end reason.' });
      return;
    }
    if (!finalDate) {
      setErrors({ finalDate: 'A final service date is required — it is the date the close runs.' });
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      const args: {
        p_facility_id: string;
        p_end_reason_code: string;
        p_final_service_date: string;
        p_note?: string;
        p_attribution?: string;
      } = {
        p_facility_id: facilityId,
        p_end_reason_code: endReasonCode,
        p_final_service_date: finalDate,
      };
      if (note.trim()) args.p_note = note.trim();
      if (attributionOverride) args.p_attribution = attributionOverride;

      const data = await endFacility(args);
      toast.success('Termination opened', {
        description:
          `${typeof data === 'string' ? `${data} — ` : ''}` +
          'Finance and HR acknowledge, then an executive approves. Nothing closes until the final service date.',
        duration: 0,
      });
      // The ack chain lives on THIS page (the Actions tab no longer carries it,
      // removed 2026-08-21). Stay, reset the form, and re-read so the new
      // termination's panel appears right here.
      setBranch(null);
      setConfirmTerminate(false);
      setEndReasonCode('');
      setFinalDate('');
      setNote('');
      setAttributionOverride('');
      setRecordKey((k) => k + 1);
      await load();
    } catch (err) {
      const { field, message } = fieldErrorFrom(err, 'Could not open a termination.');
      setErrors({ [field]: message });
      setConfirmTerminate(false);
    } finally {
      setBusy(false);
    }
  }

  // ----- Render ------------------------------------------------------------

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl p-4 md:p-6">
        <Skeleton width="100%" height={72} block />
        <div className="mt-4">
          <Skeleton width="100%" height={220} block />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-3xl p-4 md:p-6">
        <BackToFacility href={facilityHref} />
        <ErrorState title="Couldn’t load this facility" body={loadError} />
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="mx-auto max-w-3xl p-4 md:p-6">
        <BackToFacility href={facilityHref} />
        <ErrorState
          title="No such facility"
          body={`Nothing found for ${facilityId ?? '(no id in the URL)'}.`}
        />
      </div>
    );
  }

  const payerLabel = accountName ?? facility.account_id ?? 'an unnamed account';

  // "—/mo" is a false statement on a hospital billed on actual hours, and "$0"
  // is never a valid render for a missing rate (TCSD-0049). One helper decides.
  const rate = clientRateDisplay({
    basis: rateBasis ?? deriveRateBasisFallback(facility.monthly_rate),
    monthlyRate: facility.monthly_rate,
  });

  // Planned vs actual, for Finance. Planned drives forecasting; actual drives
  // paying the sub. See lifecycleDateRows.
  const dateRows = lifecycleDateRows({
    serviceState: facility.service_state,
    status: facility.status,
    serviceStateChangedAt: facility.service_state_changed_at,
    expectedResumeDate: facility.expected_resume_date,
    closedDate: facility.closed_date,
    terminationFinalServiceDate: datedTermination?.final_service_date ?? null,
    terminationStatus: datedTermination?.status ?? null,
  });

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-6">
      <BackToFacility href={facilityHref} />

      <PageHeader
        eyebrow={
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono">{facility.facility_id}</span>
            <span aria-hidden="true">·</span>
            <span>{payerLabel}</span>
            {facility.account_manager_name && (
              <>
                <span aria-hidden="true">·</span>
                <span>AM: {facility.account_manager_name}</span>
              </>
            )}
          </span>
        }
        title={<span>Stop service — {facility.facility_name}</span>}
        meta={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <ServiceStateChip state={facility.service_state} />
            <span>
              Record: <span className="font-medium text-tcs-text">{facility.status ?? '—'}</span>
            </span>
            <span title={rate.hint}>
              Billing: <span className="font-medium text-tcs-text">{rate.text}</span>
            </span>
          </span>
        }
      />

      {dateRows.length > 0 && (
        <LifecycleDates
          rows={dateRows}
          className="mt-3 rounded-lg border border-tcs-border bg-tcs-bg px-3 py-2"
        />
      )}

      {/* The one sentence that keeps the two axes apart on screen. It is here
          rather than in a tooltip because conflating them is the documented
          way this goes wrong. */}
      <p className="mt-4 flex items-start gap-2 rounded-lg border border-tcs-border bg-tcs-bg-muted px-3 py-2.5 text-[12.5px] text-tcs-text-secondary">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          This stops service at <strong className="text-tcs-text">this one facility</strong>. It
          does not close {payerLabel} — the account keeps its history and stays open for the sales
          team even with no revenue on it.
        </span>
      </p>

      {/* ---- Terminal states: nothing to do here -------------------------- */}
      {isRecordClosed && (
        <StateNotice
          tone="danger"
          icon={<CircleSlash className="h-4 w-4" />}
          title={`This facility is ${facility.status}.`}
        >
          <p>
            Service ended {fmtDate(facility.closed_date)}
            {/* close_reason is free text and usually already ends in a full
                stop ("The moved to a new location."), so appending one gave
                "location..". Strip whatever terminal punctuation is there and
                supply exactly one. */}
            {facility.close_reason
              ? ` — ${facility.close_reason.trim().replace(/[.!?]+$/, '')}`
              : ''}
            . There is nothing left to pause or terminate. If service is genuinely restarting, the
            closure has to be restored first (that is an executive action, not this screen).
          </p>
        </StateNotice>
      )}

      {!isRecordClosed && isEnded && (
        <StateNotice
          tone="warn"
          icon={<CircleSlash className="h-4 w-4" />}
          title="Service is already recorded as Ended."
        >
          <p>
            The record is still {facility.status ?? 'open'}, so the scheduled close has not run yet.
            {openTerminationCoversThis && openTermination
              ? ` Termination ${openTermination.termination_id} is waiting on its acknowledgements.`
              : ''}{' '}
            The acknowledgement chain is in the panel below.
          </p>
        </StateNotice>
      )}

      {/* ---- Already paused: resume is the primary action ----------------- */}
      {filledFromAskAi && (
        <p className="mt-4 rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm text-tcs-text">
          Filled in from Ask AI. Check it, change anything, and press the button below. Nothing has been saved yet.
        </p>
      )}

      {!isRecordClosed && isPaused && (
        <StateNotice
          tone="info"
          icon={<PauseCircle className="h-4 w-4" />}
          title={`Service is paused${activePauseReasonLabel ? ` — ${activePauseReasonLabel}` : ''}.`}
        >
          <ul className="space-y-0.5">
            <li>
              Paused since{' '}
              <span className="font-medium text-tcs-text">
                {fmtDate(facility.service_state_changed_at)}
              </span>
              .
            </li>
            <li>
              {facility.expected_resume_date
                ? `Expected back ${fmtDate(facility.expected_resume_date)}.`
                : 'No expected resume date — this pause is open-ended.'}
            </li>
            <li>{pausedRateSentence(rate)}</li>
          </ul>

          <div className="mt-3 rounded-md border border-tcs-border bg-tcs-bg px-3 py-2">
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-tcs-text">
                Note (optional) — what changed
              </span>
              <input
                type="text"
                value={resumeNote}
                onChange={(e) => setResumeNote(e.target.value)}
                className={INPUT_CLASS}
                placeholder="Renovation finished two weeks early."
              />
            </label>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                icon={<PlayCircle className="h-4 w-4" />}
                loading={busy}
                disabled={!caps.canOfferResume}
                onClick={() => void submitResume()}
              >
                Resume service
              </Button>
              <span className="text-xs text-tcs-text-muted">
                Brings back exactly the assignments this pause suspended, and retracts the
                stop-billing notice it raised.
              </span>
            </div>
            <FieldError message={errors.form} />
          </div>
        </StateNotice>
      )}

      {/* ---- This facility is already in an open termination ---------------
          One open termination per FACILITY (2026-10-13). A termination on a
          sibling facility is not a blocker and appears under "Other closures
          on this account" instead. The ack chain is the panel directly below —
          the Actions tab no longer carries it (removed 2026-08-21). */}
      {!isRecordClosed && openTermination && !isEnded && (
        <StateNotice
          tone="warn"
          icon={<AlertTriangle className="h-4 w-4" />}
          title={
            openTermination.scope === 'account'
              ? `${payerLabel} has a whole-account termination open (${openTermination.termination_id}).`
              : `This facility is already in an open termination (${openTermination.termination_id}).`
          }
        >
          <p>
            {openTermination.scope === 'account'
              ? 'It covers every facility on this account, including this one.'
              : `Last service ${
                  openTermination.final_service_date
                    ? fmtDate(openTermination.final_service_date)
                    : 'not set yet'
                }.`}{' '}
            Each facility can be in only one open termination. Acknowledge, re-date or cancel it
            below. Pausing this facility is unaffected.
          </p>
        </StateNotice>
      )}

      {/* ---- The termination covering THIS facility: acknowledge, date it,
          approve, cancel. Renders nothing when there is no record. Keyed so it
          re-reads after a termination is opened on this page. */}
      <TerminationRecord
        key={recordKey}
        accountId={facility.account_id ?? null}
        facilityId={facility.facility_id}
        accountName={accountName}
        onChanged={() => void load()}
      />

      <OtherClosures rows={otherClosures} />

      {/* ---- The branching question -------------------------------------- */}
      {!isRecordClosed && !isEnded && (
        <div className="mt-4 space-y-4">
          <fieldset className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
            <legend className="px-1 text-sm font-semibold text-tcs-text">
              Is service stopping for now, or for good?
            </legend>

            <BranchOption
              name="stop-service-branch"
              checked={branch === 'pause'}
              disabled={!caps.canOfferPause}
              onSelect={() => {
                setBranch('pause');
                setErrors({});
              }}
              icon={<PauseCircle className="h-5 w-5" />}
              title="Pause — service stops, the relationship does not"
              body={
                isPaused
                  ? 'Already paused. Resume it above before pausing again.'
                  : PAUSE_BRANCH_BODY
              }
            />

            {/* When terminate is unavailable, the reason replaces the copy
                rather than sitting only in the styling — a disabled control
                with no explanation is TCSD-0030's dead end. */}
            <BranchOption
              name="stop-service-branch"
              checked={branch === 'terminate'}
              disabled={!caps.canOfferTerminate}
              onSelect={() => {
                setBranch('terminate');
                setErrors({});
              }}
              icon={<CircleSlash className="h-5 w-5" />}
              title="Terminate — service ends for good"
              body={caps.terminateBlockedReason ?? TERMINATE_BRANCH_BODY}
            />
            <FieldError message={errors.branch} />
          </fieldset>

          {/* ---- Pause form ------------------------------------------------ */}
          {branch === 'pause' && caps.canOfferPause && (
            <div className="space-y-3 rounded-xl border border-tcs-border bg-tcs-bg p-4">
              <h2 className="text-sm font-semibold text-tcs-text">Pause service</h2>

              <label className="block text-sm">
                <span className="mb-1 block text-xs font-medium text-tcs-text">
                  Why <span className="text-rag-red">*</span>
                </span>
                <select
                  value={pauseReasonCode}
                  onChange={(e) => {
                    setPauseReasonCode(e.target.value);
                    clearError('pauseReason');
                  }}
                  className={INPUT_CLASS}
                >
                  <option value="">Choose one…</option>
                  {pauseReasons.map((r) => (
                    <option key={r.reason_code} value={r.reason_code}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-[11px] text-tcs-text-muted">
                  A fixed list on purpose — the reason sets how the relationship is reported.
                </span>
                <FieldError message={errors.pauseReason} />
              </label>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block text-xs font-medium text-tcs-text">
                    Expected resume date
                  </span>
                  <input
                    type="date"
                    value={resumeDate}
                    onChange={(e) => {
                      setResumeDate(e.target.value);
                      clearError('resumeDate');
                    }}
                    className={INPUT_CLASS}
                  />
                  <FieldError message={errors.resumeDate} />
                </label>
                <div className="flex items-end pb-1 text-[11px] text-tcs-text-muted">
                  Either a date or a note is required. An open-ended pause with no explanation is
                  how a paused client turns into an unrecorded loss.
                </div>
              </div>

              <label className="block text-sm">
                <span className="mb-1 block text-xs font-medium text-tcs-text">Note</span>
                <textarea
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    clearError('resumeDate');
                  }}
                  rows={3}
                  className={INPUT_CLASS}
                  placeholder="What did the client actually say? Finance and the AM both read this."
                />
                <FieldError message={errors.note} />
              </label>

              <ReasonDefaults reason={pauseReasonRow} />

              <OverridesDisclosure
                open={showOverrides}
                onToggle={() => setShowOverrides((v) => !v)}
                showIntent
                intentValue={intentOverride}
                onIntentChange={(v) => {
                  setIntentOverride(v);
                  clearError('intent');
                }}
                attributionValue={attributionOverride}
                onAttributionChange={(v) => {
                  setAttributionOverride(v);
                  clearError('attribution');
                }}
                intentError={errors.intent}
                attributionError={errors.attribution}
              />

              <FieldError message={errors.form} />

              <div className="flex flex-wrap items-center gap-2 border-t border-tcs-border pt-3">
                <Button
                  icon={<PauseCircle className="h-4 w-4" />}
                  loading={busy}
                  onClick={() => void submitPause()}
                >
                  Pause service
                </Button>
                <Button variant="ghost" as={Link} to={facilityHref}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* ---- Terminate form -------------------------------------------- */}
          {branch === 'terminate' && caps.canOfferTerminate && (
            <div className="space-y-3 rounded-xl border border-st-danger-border bg-tcs-bg p-4">
              <h2 className="text-sm font-semibold text-tcs-text">End service for good</h2>

              <p className="rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
                Ending service records the relationship as over and hard-ends the sub’s assignment
                on the final service date. If the client is coming back — even in six months —
                pause instead: a pause keeps the rate, the crew and the record.
              </p>

              <label className="block text-sm">
                <span className="mb-1 block text-xs font-medium text-tcs-text">
                  End reason <span className="text-rag-red">*</span>
                </span>
                <select
                  value={endReasonCode}
                  onChange={(e) => {
                    setEndReasonCode(e.target.value);
                    clearError('endReason');
                  }}
                  className={INPUT_CLASS}
                >
                  <option value="">Choose one…</option>
                  {endReasons.map((r) => (
                    <option key={r.reason_code} value={r.reason_code}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-[11px] text-tcs-text-muted">
                  This drives whether the site counts as lost or retained, and who it is attributed
                  to. A fixed list on purpose.
                </span>
                <FieldError message={errors.endReason} />
              </label>

              <label className="block text-sm">
                <span className="mb-1 block text-xs font-medium text-tcs-text">
                  Final service date <span className="text-rag-red">*</span>
                </span>
                <input
                  type="date"
                  value={finalDate}
                  onChange={(e) => {
                    setFinalDate(e.target.value);
                    clearError('finalDate');
                  }}
                  className={INPUT_CLASS}
                />
                <span className="mt-1 block text-[11px] text-tcs-text-muted">
                  The last day we clean. The close runs on this date, not today — and only once
                  Finance, HR and an executive have signed off.
                </span>
                <FieldError message={errors.finalDate} />
              </label>

              <label className="block text-sm">
                <span className="mb-1 block text-xs font-medium text-tcs-text">
                  Notes for Finance and HR
                </span>
                <textarea
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    clearError('note');
                  }}
                  rows={3}
                  className={INPUT_CLASS}
                  placeholder="What happened, in the words you would use to explain it to Finance?"
                />
                <FieldError message={errors.note} />
              </label>

              <ReasonDefaults reason={endReasonRow} />

              <OverridesDisclosure
                open={showOverrides}
                onToggle={() => setShowOverrides((v) => !v)}
                showIntent={false}
                intentValue=""
                onIntentChange={() => undefined}
                attributionValue={attributionOverride}
                onAttributionChange={(v) => {
                  setAttributionOverride(v);
                  clearError('attribution');
                }}
                attributionError={errors.attribution}
              />

              <FieldError message={errors.form} />

              <div className="flex flex-wrap items-center gap-2 border-t border-tcs-border pt-3">
                {confirmTerminate ? (
                  <>
                    <Button
                      variant="destructive"
                      icon={<CircleSlash className="h-4 w-4" />}
                      loading={busy}
                      onClick={() => void submitTerminate()}
                    >
                      Yes — open the termination
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmTerminate(false)}>
                      Back
                    </Button>
                    <span className="text-xs text-tcs-text-secondary">
                      {facility.facility_name} only. {payerLabel} stays open.
                    </span>
                  </>
                ) : (
                  <>
                    <Button
                      variant="destructive"
                      icon={<CircleSlash className="h-4 w-4" />}
                      disabled={busy}
                      onClick={() => setConfirmTerminate(true)}
                    >
                      End service…
                    </Button>
                    <Button variant="ghost" as={Link} to={facilityHref}>
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BackToFacility({ href }: { href: string }) {
  return (
    <div className="mb-3">
      <Link
        to={href}
        className="inline-flex items-center gap-1 text-sm text-tcs-text-secondary hover:text-tcs-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back to the facility
      </Link>
    </div>
  );
}
